import { useEffect, useState } from 'react';
import {
  collection,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { MapPin, Plus, Loader2, Check } from 'lucide-react';
import { db, functions } from '../../config/firebase';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import type { StockLocation } from '../../types/stock';
import { WRITER_SOURCE_LABELS } from '../../types/stock';

const KINDS: Array<{ id: StockLocation['kind']; label: string }> = [
  { id: 'OWN', label: 'Your studio / store' },
  { id: 'THIRD_PARTY_3PL', label: 'Warehouse / 3PL' },
  { id: 'RETAIL_CONSIGNMENT', label: 'Retail partner' },
];

export function LocationManager({ businessId }: { businessId: string }) {
  const [locations, setLocations] = useState<StockLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [kind, setKind] = useState<StockLocation['kind']>('OWN');
  const [buffer, setBuffer] = useState(0);
  const [sellable, setSellable] = useState(true);
  const [inZone, setInZone] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const q = query(
      collection(db, 'stock_locations'),
      where('businessId', '==', businessId),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setLocations(
          snap.docs
            .map((d) => d.data() as StockLocation)
            .sort((a, b) => Number(!!b.isDefault) - Number(!!a.isDefault)),
        );
        setLoading(false);
      },
      () => setLoading(false),
    );
    return unsub;
  }, [businessId]);

  const save = async (payload: Record<string, unknown>) => {
    setSaving(true);
    setError(null);
    try {
      const upsert = httpsCallable(functions, 'upsertStockLocation');
      await upsert({ businessId, ...payload });
      setShowForm(false);
      setName('');
    } catch (err: any) {
      setError(err?.message || 'Could not save location');
    }
    setSaving(false);
  };

  return (
    <Card>
      <CardContent className="p-4 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-medium text-heading">Stock locations</p>
            <p className="text-xs text-body">
              FASH only sells units from locations that are sellable and inside a delivery zone.
              Warehouse stock stays reserved for local-zone fulfillment.
            </p>
          </div>
          <Button type="button" size="sm" onClick={() => setShowForm(true)}>
            <Plus className="h-3.5 w-3.5 mr-1" />
            Add
          </Button>
        </div>

        {loading && <Loader2 className="h-4 w-4 animate-spin text-body" />}

        <div className="space-y-2">
          {locations.map((loc) => (
            <div key={loc.locationId} className="rounded-xl border border-gray-100 p-3 flex items-start gap-3">
              <MapPin className="h-4 w-4 text-primary mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-heading truncate">
                  {loc.name} {loc.isDefault ? <span className="text-[11px] text-body">· default</span> : null}
                </p>
                <p className="text-xs text-body">
                  {KINDS.find((k) => k.id === loc.kind)?.label || loc.kind}
                  {loc.sellableOnFash && loc.deliveryZoneEligible ? ' · sellable on FASH' : ' · not sellable on FASH'}
                  {` · buffer ${loc.safetyBuffer}`}
                </p>
                {loc.writerSource && (
                  <p className="text-[11px] text-body mt-1">
                    Stock here is reported by {WRITER_SOURCE_LABELS[loc.writerSource]}.{' '}
                    <button
                      type="button"
                      className="underline hover:text-heading"
                      disabled={saving}
                      onClick={() =>
                        save({
                          locationId: loc.locationId,
                          name: loc.name,
                          kind: loc.kind,
                          safetyBuffer: loc.safetyBuffer,
                          sellableOnFash: loc.sellableOnFash,
                          deliveryZoneEligible: loc.deliveryZoneEligible,
                          active: loc.active,
                          writerSource: null,
                        })
                      }
                    >
                      Switch source
                    </button>
                  </p>
                )}
              </div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={saving}
                onClick={() =>
                  save({
                    locationId: loc.locationId,
                    name: loc.name,
                    kind: loc.kind,
                    safetyBuffer: loc.safetyBuffer,
                    sellableOnFash: !loc.sellableOnFash,
                    deliveryZoneEligible: loc.deliveryZoneEligible,
                    active: loc.active,
                  })
                }
              >
                {loc.sellableOnFash ? 'Hide from FASH' : 'Sell on FASH'}
              </Button>
            </div>
          ))}
          {!loading && locations.length === 0 && (
            <p className="text-xs text-body">No locations yet. Add your studio or import from Shopify.</p>
          )}
        </div>

        {showForm && (
          <div className="rounded-xl bg-gray-50 p-3 space-y-2">
            <Input placeholder="Location name" value={name} onChange={(e) => setName(e.target.value)} />
            <select
              className="w-full text-sm border rounded-md h-9 px-2"
              value={kind}
              onChange={(e) => setKind(e.target.value as StockLocation['kind'])}
            >
              {KINDS.map((k) => (
                <option key={k.id} value={k.id}>{k.label}</option>
              ))}
            </select>
            <div className="flex items-center gap-3 text-xs">
              <label className="flex items-center gap-1">
                Buffer
                <Input
                  type="number"
                  className="w-16 h-8"
                  min={0}
                  value={buffer}
                  onChange={(e) => setBuffer(Math.max(0, Number(e.target.value)))}
                />
              </label>
              <label className="flex items-center gap-1">
                <input type="checkbox" checked={sellable} onChange={(e) => setSellable(e.target.checked)} />
                Sellable on FASH
              </label>
              <label className="flex items-center gap-1">
                <input type="checkbox" checked={inZone} onChange={(e) => setInZone(e.target.checked)} />
                In delivery zone
              </label>
            </div>
            {error && <p className="text-xs text-red-600">{error}</p>}
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                disabled={!name.trim() || saving}
                onClick={() =>
                  save({
                    name: name.trim(),
                    kind,
                    safetyBuffer: buffer,
                    sellableOnFash: sellable,
                    deliveryZoneEligible: inZone,
                    active: true,
                  })
                }
              >
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5 mr-1" />}
                Save location
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
